/* eslint no-underscore-dangle: ["error", { "allow": ["_id"] }] */
import React, { useContext, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useWebsocket from 'hooks/useWebsocket';
import { logError } from 'services/Logger';
import { ITemplate } from 'components/sessions/types';
import { getStartedByYouChats, STATUS, INotification } from 'services/getStartedByYouChats';
import { environmentUtils } from 'utils';
import NotificationPermissionModal from 'components/NotificationPermissionModal/NotificationPermissionModal';
import { NOTIFICATION_PERMISSION, OneDayInMilliseconds } from 'constants/notifications';
import useMobile from 'hooks/useMobile';
import AuthContext from '../../contexts/auth/context';
import ChatsContext from '../../contexts/chats/context';
import ContextDrawer from '../../components/ContextDrawer';
import { ModalCloseConversation } from '../../components/ModalCloseConversation';
import { ModalErrorCloseConversation } from '../../components/ModalErrorCloseConversation';
import { TimeOutModal } from '../../components/TimeOutModal';
import WebsocketContext from '../../contexts/websocket/context';
import CannedMessagesProvider from '../../contexts/cannedMessages/provider';
import QueueMenu from './QueueMenu';
import DrawerContext from '../../contexts/drawer/context';
import EmptyChatPage from './EmptyChatPage';
import SnackbarCustom, { SnackbarType } from '../../components/Snackbar';
import ContactManager from './ContactManager';
import ChatsManager from './ChatsManager';
import ServiceDashboard from './ServiceDashboard';
import MenuChatsContext from '../../contexts/menuChats/context';
import { getRoomInfo } from '../../services/getRoomInfo';
import useSegment, { googleTagManager } from '../../services/Analytics';
import Conversation from './Conversation';
import ConversationContext from '../../contexts/conversation/context';
import { STORAGE_KEYS, WEBSOCKET_EVENT_LOG_MESSAGE } from '../../constants/defaultValues';
import { ChatWrapper, DrawerWrapper, HomeContainer, MainContainer, QueueMenuWrapper } from './style';
import Chat from './Chat';
import NotificationPage from './Notification/Notification';

type AlertConditions = {
  isBrowserConnected: boolean;
  isWsConnected: boolean;
};

type AlertProps = {
  translationKeys: {
    message: string;
    title: string;
  };
  type: SnackbarType;
};

const getAlertProps = ({ isBrowserConnected, isWsConnected }: AlertConditions): AlertProps | undefined => {
  const conditions: Array<[boolean, AlertProps]> = [
    [
      !isBrowserConnected,
      {
        translationKeys: {
          title: 'browser.offline.title',
          message: 'browser.offline.message',
        },
        type: 'warning',
      },
    ],
    [
      !isWsConnected,
      {
        translationKeys: {
          title: 'websocket.reconnection.title',
          message: 'websocket.reconnection.message',
        },
        type: 'warning',
      },
    ],
  ];

  const [, options] = conditions.find(([check]) => check) || [];

  return options;
};

export const Home: React.FC = () => {
  const { chatId, notificationId } = useParams();
  const { t } = useTranslation();
  const { isMobileView } = useMobile();
  const {
    serviceSelected,
    openModalCloseChat,
    errorCloseChat,
    handleServiceSelected,
    setErrorCloseChat,
    setIsMobile,
    handleTemplateSelected,
    notificationSelected,
  } = useContext(ChatsContext);

  const { currentUserInfo } = useContext(AuthContext);
  const { handleTabSelection: updateDrawerActiveTab, selectedTab: selectedDrawerTab } = useContext(DrawerContext);
  const { openContactManager, openChatsManager, openServiceDashboard } = useContext(MenuChatsContext);
  const { openStartNewConversation, handleStartNewConversation } = useContext(ConversationContext);
  const {
    browserConnection: isBrowserConnected,
    showSnackbar,
    snackBarMessage,
    handleSnackBar,
    snackBarType,
    websocketIsConnect,
    websocketHasConnected,
  } = useContext(WebsocketContext);

  const [openSnackbarSuccess, setOpenSnackbarSuccess] = useState(false);
  const [openPermissionModal, setOpenPermissionModal] = useState(false);
  const { sendPage, sendConversationSelectedEvent } = useSegment();
  const { webSocket, isConnected: isWsConnected } = useWebsocket();
  const alertOptions = getAlertProps({ isWsConnected, isBrowserConnected });
  const serviceId = serviceSelected?._id;
  const notificationSelectedId = notificationSelected?._id;

  const loadRoomInfo = async () => {
    if (chatId) {
      try {
        const {
          data: { room = {} },
        } = await getRoomInfo(chatId);
        handleServiceSelected(room);
        sendConversationSelectedEvent(room);
        googleTagManager.pushEvent({
          event: 'salesdesk-view_conversation',
          user_name: currentUserInfo.email,
          account_name: environmentUtils.getEnvironmentInstance(),
        });
      } catch (error) {
        logError('Error while fetching Room Info', error);
      }
    } else {
      handleServiceSelected(null);
    }
    sendPage();
  };

  const convertNotificationToRoom = (notification: INotification) => ({
    _id: notification.id,
    lastMessage: {
      _id: notification.template.id,
      msg: '',
      t: notification.template.name,
      ts: notification.created_at || '',
      unread: false,
      u: {
        _id: notification.contact.id,
        username: notification.contact.name || '',
        name: notification.contact.name || '',
      },
    },
    fname: notification.contact.name || '',
    tags: [],
    unreadChat: false,
    unreadMessages: 0,
    hasDiscussion: false,
  });

  const loadNotificationInfo = async () => {
    if (notificationId) {
      try {
        const userDepartments = currentUserInfo.departments
          .filter((item) => item.departmentEnabled)
          .map((item) => item.departmentId);
        const {
          data: { notifications = [] },
        } = await getStartedByYouChats(userDepartments);
        let filteredNotification: INotification | null;
        let templateSelected: ITemplate;
        if (notifications && notifications.length > 0) {
          filteredNotification =
            notifications.find(
              (notificationItem: INotification) =>
                notificationItem.status === STATUS.SENT && notificationItem.id === notificationId
            ) || null;
          if (!filteredNotification) {
            return;
          }
          templateSelected = convertNotificationToRoom(filteredNotification);
          handleTemplateSelected(templateSelected);
        }
      } catch (error) {
        logError('Error while fetching Notification Info', error);
      }
    } else {
      handleTemplateSelected(null);
    }
  };

  const handleSnackbar = () => {
    setOpenSnackbarSuccess(false);
    handleSnackBar();
  };

  useEffect(() => {
    setIsMobile(isMobileView);
  }, [isMobileView, setIsMobile]);

  useEffect(() => {
    window.sessionStorage.removeItem('drawerOpen');

    if (!serviceId) {
      updateDrawerActiveTab(null);
    }

    if (openStartNewConversation && serviceId) {
      handleStartNewConversation(false);
    }
  }, [serviceId]);

  useEffect(() => {
    if (websocketIsConnect && websocketHasConnected && chatId) {
      loadRoomInfo();
    }
  }, [chatId, websocketIsConnect, websocketHasConnected]);

  useEffect(() => {
    if (notificationId && currentUserInfo) {
      loadNotificationInfo();
    }
  }, [notificationId, currentUserInfo]);

  useEffect(() => {
    if (showSnackbar) {
      setOpenSnackbarSuccess(true);
    }
  }, [showSnackbar]);

  useEffect(() => {
    const checkNotificationStatus = () => {
      const { permission } = Notification;

      if (permission !== NOTIFICATION_PERMISSION.GRANTED) {
        const lastDismissed = localStorage.getItem(STORAGE_KEYS.PERMISSION_DENIED);
        const now = Date.now();
        if (!lastDismissed || now - Number(lastDismissed) > OneDayInMilliseconds) {
          setOpenPermissionModal(true);
        }
      }
    };

    // Verify if the Notification API is available since iOS doesn't support it in browsers
    const notificationApiExists = 'Notification' in window && 'permission' in Notification;
    if (isMobileView && notificationApiExists) {
      checkNotificationStatus();
    }
  }, [isMobileView]);

  const connectionAlertDelay = Number.parseInt(process.env.CONNECTION_ALERT_DELAY || '5000', 10);

  let initiallySelectedTab;

  const shouldShowQueueMenu =
    !selectedDrawerTab &&
    !serviceId &&
    !openContactManager &&
    !openChatsManager &&
    !openStartNewConversation &&
    !notificationSelectedId;

  const shouldShowChat =
    (!selectedDrawerTab && serviceId) ||
    openContactManager ||
    openChatsManager ||
    openStartNewConversation ||
    (!selectedDrawerTab && !!notificationSelectedId);

  return (
    <HomeContainer data-testid="home">
      {alertOptions && (
        <SnackbarCustom
          data-testid="salesdesk-ws-alert"
          logName={WEBSOCKET_EVENT_LOG_MESSAGE.RECONNECTION_BFF}
          open={Boolean(alertOptions)}
          title={t(alertOptions.translationKeys.title)}
          message={t(alertOptions.translationKeys.message)}
          type={alertOptions.type}
          delayMillis={connectionAlertDelay}
        />
      )}
      <MainContainer>
        <QueueMenuWrapper className={shouldShowQueueMenu ? 'show' : ''}>
          <QueueMenu />
        </QueueMenuWrapper>
        <ChatWrapper className={shouldShowChat ? 'show' : ''}>
          {openContactManager ? (
            <ContactManager />
          ) : openChatsManager ? (
            <ChatsManager initialTab={initiallySelectedTab} />
          ) : openStartNewConversation ? (
            <Conversation />
          ) : openServiceDashboard ? (
            <ServiceDashboard />
          ) : (
            <CannedMessagesProvider>
              {serviceSelected?._id && (
                <Chat chatId={serviceSelected._id} webSocket={webSocket} isWsConnected={isWsConnected} />
              )}
              {notificationSelectedId && <NotificationPage />}
              {!serviceSelected?._id && !notificationSelectedId && <EmptyChatPage />}
              {openSnackbarSuccess && (
                <SnackbarCustom
                  hasTextButton
                  open={openSnackbarSuccess}
                  message={t(snackBarMessage) || ''}
                  handleClose={handleSnackbar}
                  type={snackBarType || 'success'}
                />
              )}
            </CannedMessagesProvider>
          )}
        </ChatWrapper>

        {selectedDrawerTab && (
          <DrawerWrapper>
            <ContextDrawer open={!!selectedDrawerTab} tabSelect={selectedDrawerTab} />
          </DrawerWrapper>
        )}

        {openModalCloseChat && <ModalCloseConversation open={openModalCloseChat} />}
        {openPermissionModal && (
          <NotificationPermissionModal open={openPermissionModal} handleClose={() => setOpenPermissionModal(false)} />
        )}
        {errorCloseChat && (
          <ModalErrorCloseConversation openError={errorCloseChat} handleErrorCloseChat={setErrorCloseChat} />
        )}

        <TimeOutModal />
      </MainContainer>
    </HomeContainer>
  );
};
